import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, User, Bot, ChevronRight, ArrowDown, ArrowUp } from 'lucide-react';

const LandingChatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            content: "Hi! 👋 I'm here to help you learn about ExpenseTracker. What would you like to know?",
            timestamp: new Date()
        }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const messagesEndRef = useRef(null);

    const predefinedOptions = [
        {
            id: 'features',
            text: '💡 What can this app do?',
            response: "ExpenseTracker helps you:\n\n• Track income & expenses in real-time\n• Set and monitor budgets\n• Get visual insights with charts\n• Sync across all your devices\n• Keep your data secure with bank-level encryption\n\nWould you like to know more about any specific feature?"
        },
        {
            id: 'signup',
            text: '🚀 How do I sign up?',
            response: "Getting started is super easy!\n\n1. Click 'Get Started' or 'Start Free Trial'\n2. Enter your email and create a password\n3. Verify your email\n4. Start tracking immediately!\n\nYou get a 30-day free trial with all premium features. No credit card required! 🎉"
        },
        {
            id: 'security',
            text: '🔒 Is my data safe?',
            response: "Absolutely! Your security is our top priority:\n\n• Bank-level encryption (256-bit SSL)\n• Data encrypted in transit and at rest\n• We never store bank login credentials\n• Regular security audits\n• GDPR compliant\n\nYour financial data is as secure as online banking! 🛡️"
        },
        {
            id: 'add-transaction',
            text: '📱 How to add income/expense?',
            response: "Adding transactions is super simple:\n\n• **One-click adding** from the main dashboard\n• **Quick categories** (Food, Transport, Bills, etc.)\n• **Smart auto-categorization** learns your patterns\n• **Photo receipts** - snap and save\n• **Bulk import** from bank statements\n\nMost users add an expense in under 10 seconds! ⚡"
        },
        {
            id: 'pricing',
            text: '💰 What does it cost?',
            response: "We offer flexible pricing:\n\n• **Free Trial**: 30 days, all features included\n• **Basic Plan**: $4.99/month - Perfect for individuals\n• **Pro Plan**: $9.99/month - Advanced analytics & reports\n• **Family Plan**: $14.99/month - Up to 5 users\n\nAll plans include unlimited transactions and device sync! 📊"
        },
        {
            id: 'support',
            text: '🤝 Need more help?',
            response: "I'm here whenever you need me! You can also:\n\n• Check our comprehensive FAQ section\n• Watch our quick demo video\n• Email us at support@expensetracker.com\n• Join our community forum\n\nOur support team typically responds within 2 hours during business hours! 📧"
        }
    ];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleOptionClick = (option) => {
        // Add user message
        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: option.text,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsTyping(true);

        // Simulate bot typing delay
        setTimeout(() => {
            const botMessage = {
                id: Date.now() + 1,
                type: 'bot',
                content: option.response,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botMessage]);
            setIsTyping(false);
        }, 1000);
    };

    const resetChat = () => {
        setMessages([
            {
                id: 1,
                type: 'bot',
                content: "Hi! 👋 I'm here to help you learn about ExpenseTracker. What would you like to know?",
                timestamp: new Date()
            }
        ]);
        setShowMoreOptions(false);
    };

    return (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
            {/* Chat Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        position: 'relative'
                    }}
                    onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                        e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.6)';
                    }}
                    onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                    }}
                >
                    <MessageCircle size={24} />
                    {/* Pulsing notification dot */}
                    <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '12px',
                        height: '12px',
                        background: '#FFD700',
                        borderRadius: '50%',
                        animation: 'pulse 2s infinite'
                    }} />
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div style={{
                    width: '380px',
                    height: '500px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s ease-out'
                }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '20px',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Bot size={20} />
                            </div>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '16px' }}>
                                    ExpenseTracker Assistant
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                    Online • Instant replies
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            style={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '35px',
                                height: '35px',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                            }}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div style={{
                        flex: 1,
                        padding: '20px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                    }}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                                    alignItems: 'flex-start',
                                    gap: '10px'
                                }}
                            >
                                {message.type === 'bot' && (
                                    <div style={{
                                        width: '30px',
                                        height: '30px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <Bot size={16} color="white" />
                                    </div>
                                )}

                                <div style={{
                                    maxWidth: '70%',
                                    padding: '12px 16px',
                                    borderRadius: message.type === 'user' ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                                    background: message.type === 'user'
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : 'rgba(102, 126, 234, 0.1)',
                                    color: message.type === 'user' ? 'white' : '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    whiteSpace: 'pre-line',
                                    boxShadow: message.type === 'user'
                                        ? '0 4px 15px rgba(102, 126, 234, 0.3)'
                                        : 'none'
                                }}>
                                    {message.content}
                                </div>

                                {message.type === 'user' && (
                                    <div style={{
                                        width: '30px',
                                        height: '30px',
                                        background: '#f0f0f0',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <User size={16} color="#666" />
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Typing Indicator */}
                        {isTyping && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <div style={{
                                    width: '30px',
                                    height: '30px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Bot size={16} color="white" />
                                </div>
                                <div style={{
                                    padding: '12px 16px',
                                    borderRadius: '18px 18px 18px 6px',
                                    background: 'rgba(102, 126, 234, 0.1)',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center'
                                }}>
                                    <div style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: '#667eea',
                                        animation: 'typing 1.4s infinite ease-in-out'
                                    }} />
                                    <div style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: '#667eea',
                                        animation: 'typing 1.4s infinite ease-in-out 0.2s'
                                    }} />
                                    <div style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        background: '#667eea',
                                        animation: 'typing 1.4s infinite ease-in-out 0.4s'
                                    }} />
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Options/Input Area */}
                    <div style={{
                        padding: '20px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                        background: 'rgba(255, 255, 255, 0.5)'
                    }}>
                        {/* Quick Options */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            marginBottom: '15px'
                        }}>
                            {predefinedOptions.slice(0, 3).map((option) => (
                                <button
                                    key={option.id}
                                    onClick={() => handleOptionClick(option)}
                                    disabled={isTyping}
                                    style={{
                                        background: 'white',
                                        border: '1px solid rgba(102, 126, 234, 0.2)',
                                        borderRadius: '12px',
                                        padding: '10px 12px',
                                        fontSize: '13px',
                                        color: '#667eea',
                                        cursor: isTyping ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.2s ease',
                                        opacity: isTyping ? 0.5 : 1
                                    }}
                                    onMouseOver={(e) => {
                                        if (!isTyping) {
                                            e.target.style.background = 'rgba(102, 126, 234, 0.05)';
                                            e.target.style.borderColor = 'rgba(102, 126, 234, 0.4)';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!isTyping) {
                                            e.target.style.background = 'white';
                                            e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                                        }
                                    }}
                                >
                                    <span>{option.text}</span>
                                    <ChevronRight size={14} />
                                </button>
                            ))}
                        </div>

                        {/* More Options Toggle */}
                        <div style={{ marginBottom: '10px' }}>
                            <button
                                onClick={() => setShowMoreOptions(!showMoreOptions)}
                                style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    color: '#667eea',
                                    fontWeight: '600',
                                    padding: '5px 0',
                                    textAlign: 'center'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', justifyContent: 'center' }}>
                                    {showMoreOptions ? 'Less Questions' : 'More Questions'}
                                    {showMoreOptions ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                </span>
                            </button>
                            {showMoreOptions && (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    marginTop: '10px'
                                }}>
                                    {predefinedOptions.slice(3).map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => handleOptionClick(option)}
                                            disabled={isTyping}
                                            style={{
                                                background: 'white',
                                                border: '1px solid rgba(102, 126, 234, 0.2)',
                                                borderRadius: '10px',
                                                padding: '8px 10px',
                                                fontSize: '12px',
                                                color: '#667eea',
                                                cursor: isTyping ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                transition: 'all 0.2s ease',
                                                opacity: isTyping ? 0.5 : 1
                                            }}
                                            onMouseOver={(e) => {
                                                if (!isTyping) {
                                                    e.target.style.background = 'rgba(102, 126, 234, 0.05)';
                                                }
                                            }}
                                            onMouseOut={(e) => {
                                                if (!isTyping) {
                                                    e.target.style.background = 'white';
                                                }
                                            }}
                                        >
                                            <span>{option.text}</span>
                                            <ChevronRight size={12} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Reset Chat */}
                        <button
                            onClick={resetChat}
                            style={{
                                width: '100%',
                                background: 'transparent',
                                border: 'none',
                                color: '#999',
                                fontSize: '11px',
                                cursor: 'pointer',
                                padding: '5px',
                                textAlign: 'center'
                            }}
                        >
                            Start New Conversation
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.7;
                        transform: scale(1.1);
                    }
                }

                @keyframes typing {
                    0%, 80%, 100% {
                        transform: scale(0.8);
                        opacity: 0.5;
                    }
                    40% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                /* Mobile responsiveness */
                @media (max-width: 768px) {
                    .chat-window {
                        width: calc(100vw - 40px) !important;
                        height: calc(100vh - 40px) !important;
                        position: fixed !important;
                        top: 20px !important;
                        right: 20px !important;
                        left: 20px !important;
                        bottom: 20px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default LandingChatbot;